import { imageDataZod } from 'packages/lib/api/annotations/contract/image-data';
import { lidarExtractionZod } from 'packages/lib/api/annotations/contract/lidar-extraction';
import { z } from 'zod';
import {
  AnnotationState,
  TaskState,
  annotationPartnerZod,
  annotationStateZod,
  taskPriorityZod,
  taskSampleTypeZod,
  taskStateZod,
  taskTypeZodWithCatch,
} from 'packages/lib/api/annotations/contract/enums';
import { videoExtractionZod } from 'packages/lib/api/annotations/contract/video-extraction';
import { stateMachineMap } from 'packages/task-man/service/state-machine/constants';
// May-20-2026 Used Cursor (Auto / Claude Opus 4.7 / Sonnet 4.6) to fork code for single-task SE

export const TASK_COMMENTS_MAX_CHAR_LENGTH = 1024;

const baseTaskZod = z.object({
  token: z.string(),
  type: taskTypeZodWithCatch,
  /** For legacy Scene Enrichment / Classification Enrichment tasks, where each task is associated with a single image, this field is used to retrieve the image for this task */
  imageDataToken: z.nullable(z.string()),
  lidarExtractionToken: z.nullable(z.string()),
  audioExtractionToken: z.nullable(z.string()),
  videoExtractionToken: z.nullable(z.string()),
  /** For the new single-task version of Scene Enrichment, imageExtractionToken can be used to fetch tokens for all images associated with the task */
  imageExtractionToken: z.nullable(z.string()),
  sampleType: taskSampleTypeZod,
  partner: annotationPartnerZod,
  taskGroupToken: z.nullable(z.string()),
  name: z.nullable(z.string()),
  startFrame: z.number().int().nonnegative(),
  endFrame: z.number().int().nonnegative(),
  assignee: z.nullable(z.string()),
  rawAnnotationToken: z.nullable(z.string()),
  redoCount: z.number().int().nonnegative(),
  populatedAt: z.nullable(z.string().datetime()),
  lastCallbackAt: z.nullable(z.string().datetime()),
  doneAt: z.nullable(z.string().datetime()),
  comments: z.nullable(
    z.string().max(TASK_COMMENTS_MAX_CHAR_LENGTH, {
      message: `Backend is currently limited to ${TASK_COMMENTS_MAX_CHAR_LENGTH} characters`,
    })
  ),
  reviewedBy: z.nullable(z.string()),
  instructionToken: z.nullable(z.string()),
  tag: z.nullable(z.string()),
  state: taskStateZod,
  annotationState: annotationStateZod,
  priority: taskPriorityZod,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  imageData: z.nullable(imageDataZod),
  lidarExtraction: z.nullable(lidarExtractionZod),
  videoExtraction: z.nullable(videoExtractionZod),
  // audioExtraction:, not currently used
});

export const taskZod = baseTaskZod.catch((ctx) => {
  console.error(
    'A task failed Zod validation. This may be due to an unrecognized "type" from the backend. See error and input data below.',
    { error: ctx.error, input: ctx.input }
  );
  // To prevent the entire API call from failing, we return the original input.
  // It's cast to `Task` to satisfy the type system, but be aware it's not a fully-validated object.
  return ctx.input as Task;
});

export type Task = z.infer<typeof baseTaskZod>;
export const tasksRespZod = z.array(taskZod);
export type TasksResp = z.infer<typeof tasksRespZod>;

export const isTask = (task: unknown): task is Task =>
  baseTaskZod.safeParse(task).success;

/** Internal (iMerit) tasks use partner None. External partners such as Scale own assignment. */
export const isInternalTask = (task: {
  partner?: Task['partner'] | null;
}): boolean => task.partner === annotationPartnerZod.enum.None;

export const taskCountRespZod = z.object({
  '*': z.number(),
});

export type TaskCountResponse = z.infer<typeof taskCountRespZod>;

/** Next annotation state is determined by stateMachine's SUBMIT or ASSIGN */
export const nextAnnotationStateMap: Partial<
  Record<AnnotationState, AnnotationState>
> = Object.entries(stateMachineMap.states).reduce((reducedStates, cur) => {
  const [key, value] = cur;
  if (!!value.SUBMIT || !!value.ASSIGN) {
    reducedStates[key as AnnotationState] = value.SUBMIT
      ? value.SUBMIT
      : value.ASSIGN;
  }
  return reducedStates;
}, {} as Partial<Record<AnnotationState, AnnotationState>>);

// Inverse of nextAnnotationStateMap: values swapped with keys
export const prevAnnotationStateMap: Partial<
  Record<AnnotationState, AnnotationState>
> = Object.fromEntries(
  Object.entries(nextAnnotationStateMap).map(([key, value]) => [value, key])
);

/** A submissible annotation state is one whose object contains a SUBMIT key */
export const submissibleAnnotationStates: ReadonlyArray<AnnotationState> =
  Object.entries(stateMachineMap.states)
    .filter(([key, value]) => !!value.SUBMIT)
    .map((item) => {
      const [key] = item;
      return key;
    }) as AnnotationState[];

/**
 * Maps AnnotationState to appropriate TaskState - this is a port from data-services
 * https://gitlab.ci.motional.com/team-data-services/annotations/-/blob/master/packages/lib/utils/AnnotationStateMapper.cs
 * @param annotationState
 * @returns State as TaskState
 */
export const toTaskState = (annotationState: AnnotationState): TaskState => {
  switch (annotationState) {
    case 'ToDo':
      return taskStateZod.enum.ToDo;
    case 'AnnotationInProgress':
      return taskStateZod.enum.InProgress;
    case 'AnnotationInReview':
      return taskStateZod.enum.InProgress;
    case 'QaToDo':
      return taskStateZod.enum.InReview;
    case 'QaInProgress':
      return taskStateZod.enum.InReview;
    case 'QaDone':
      return taskStateZod.enum.InReview;
    case 'AuditToDo':
      return taskStateZod.enum.InReview;
    case 'AuditInProgress':
      return taskStateZod.enum.InReview;
    case 'AuditDone':
      return taskStateZod.enum.Done;
    case 'Reopened':
      return taskStateZod.enum.InProgress;
    case 'Flagged':
      return taskStateZod.enum.InProgress;
    case 'Cancelled':
      return taskStateZod.enum.Cancelled;
    case 'Error':
      return taskStateZod.enum.Error;
    default:
      return taskStateZod.enum.Error;
  }
};
